/**
 * The About panel: a page of the deployer's own prose, over the top of an empty viewer.
 *
 * ## Where the content comes from
 *
 * A file called `about.md`, fetched at runtime from beside `index.html`. It is
 * deliberately **not part of the build**: whoever runs the site writes it, edits it, and
 * replaces it without rebuilding or redeploying anything, and a deployment that never
 * supplies one is a deployment with no About page rather than a broken one. That is why
 * the content is fetched rather than imported, and why the missing case is ordinary
 * rather than an error.
 *
 * ## When it shows itself
 *
 * On a visit with an empty library, once, until it is sent away — the rules are in
 * `domain/about.ts` along with the reasoning. After that the button in the header is how
 * it is reached, and that button is there from the first visit rather than appearing
 * after the first dismissal: a control that only exists once you no longer need it is a
 * control nobody finds.
 *
 * **Every way of closing it counts as dismissing it.** The close button, the Escape key
 * and a click on the backdrop all end up in the dialog's own `close` event, which is
 * where the preference is written. Anything else would leave somebody who pressed Escape
 * being welcomed all over again on their next visit, which reads as the site having
 * forgotten them.
 */

import {
  ABOUT_FALLBACK,
  ABOUT_FILE,
  ABOUT_STORAGE_KEY,
  aboutStorageValue,
  parseAboutDismissed,
  readAboutMarkdown,
  shouldOfferAbout,
} from '../domain/about.js';
import { parseMarkdown } from '../domain/markdown.js';
import { describeBuilds, parseBuildStamp, type BuildStamp } from '../domain/version.js';
import { logger } from '../log.js';
import { buttonElement, dialogElement, element } from './dom.js';
import { renderMarkdown } from './markdownView.js';

const log = logger('about');

export class AboutPanel {
  private readonly dialog = dialogElement('about-dialog');
  private readonly content = element('about-content');
  private readonly button = buttonElement('about-button');
  private readonly close = buttonElement('about-close');

  private readonly versions = element('about-version');

  private dismissed = parseAboutDismissed(read());

  /** This build, read once: it is substituted in at build time and cannot change. */
  private readonly pageBuild = parseBuildStamp(import.meta.env.VITE_BUILD_STAMP);

  /** Where to ask what build the service is, once there is a service to ask. */
  private service: (() => Promise<BuildStamp | undefined>) | undefined;

  private serviceBuild: BuildStamp | undefined;

  /** The one fetch, kept so that a second press waits for it rather than repeating it. */
  private filled: Promise<void> | undefined;

  constructor() {
    this.button.addEventListener('click', () => {
      void this.open();
    });

    this.close.addEventListener('click', () => {
      this.dialog.close();
    });

    // A press that lands on the dialog element itself is a press on the backdrop: the
    // panel's own contents fill it edge to edge, so nothing inside can be the target.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialog.addEventListener('close', () => {
      this.remember();
    });

    // Before anything is fetched, so the stamp is on the panel from the first moment it
    // can be opened rather than arriving after whatever the network is doing.
    this.showVersions();
  }

  /**
   * Tell the panel where to ask what build the service is running.
   *
   * Handed over rather than reached for, because there may be no service at all: a build
   * with no API configured is a supported deployment, and one line is the right answer
   * there. Called after this panel is built, since the account client does not exist yet
   * when it is — which is also why nothing is fetched here. The ask happens the first
   * time somebody actually opens the panel.
   */
  watchService(source: () => Promise<BuildStamp | undefined>): void {
    this.service = source;
  }

  /**
   * Show the panel unprompted, if this visit is one that should.
   *
   * Takes the size of the library rather than reading it, because the caller has just
   * listed it and a second read could disagree with what is on the screen.
   */
  offer(modelCount: number): void {
    if (!shouldOfferAbout(this.dismissed, modelCount)) return;
    void this.open();
  }

  /** Show the panel because somebody asked for it. */
  async open(): Promise<void> {
    await this.fill();
    if (!this.dialog.open) this.dialog.showModal();
  }

  private async fill(): Promise<void> {
    this.filled ??= this.load();
    await this.filled;
  }

  private async load(): Promise<void> {
    const markdown = await fetchAbout();
    this.content.replaceChildren(
      ...renderMarkdown(parseMarkdown(markdown ?? ABOUT_FALLBACK)),
    );

    // Deliberately not awaited: opening the panel must not wait on a service that may be
    // slow, or absent, or answering something else entirely. The second line appears
    // under the first when it arrives. `serviceBuild` never rejects, so there is nothing
    // here to catch.
    void this.service?.().then((stamp) => {
      this.serviceBuild = stamp;
      this.showVersions();
    });
  }

  /**
   * Write the build lines out.
   *
   * `textContent` per line rather than one string with newlines in it, because a run of
   * lines is what this is and CSS should not be the only thing making it look like one.
   */
  private showVersions(): void {
    this.versions.replaceChildren(
      ...describeBuilds(this.pageBuild, this.serviceBuild).map((line) => {
        const paragraph = document.createElement('p');
        paragraph.textContent = line;
        return paragraph;
      }),
    );
  }

  private remember(): void {
    if (this.dismissed) return;
    this.dismissed = true;

    // A browser refusing to store — private browsing, or storage that is full — is not a
    // reason to refuse to close the panel. It stays closed for this session either way;
    // the only loss is that it is offered again next time.
    try {
      window.localStorage.setItem(ABOUT_STORAGE_KEY, aboutStorageValue(true));
    } catch (error) {
      log.warn('The About panel being dismissed could not be remembered.', error);
    }
  }
}

/**
 * Fetch the deployed page, or find out that there is not one.
 *
 * Never rejects. Every way this can fail — no file, a server that will not answer, a
 * server that answers with something else — comes to the same thing, which is a panel
 * with the welcome in it. Each is said out loud on the console, though, because the
 * deployer is the one person who can act on the difference between "there is no file
 * there" and "your server is serving it as HTML".
 */
async function fetchAbout(): Promise<string | undefined> {
  const url = new URL(ABOUT_FILE, document.baseURI);

  try {
    // Revalidated rather than taken from the cache, so that editing the page on the
    // server is seen by somebody who was here yesterday.
    const response = await fetch(url, { cache: 'no-cache' });

    if (!response.ok) {
      log.info(`No About page at ${url.href} (${response.status.toString()}).`);
      return undefined;
    }

    const markdown = readAboutMarkdown(
      response.headers.get('content-type'),
      await response.text(),
    );

    if (markdown === undefined) {
      log.info(
        `${url.href} did not answer with markdown, so the About panel will show the ` +
          'welcome instead. A server that answers unknown paths with index.html will do ' +
          'this; the file has to be served as itself.',
      );
    }

    return markdown;
  } catch (error) {
    log.info(`The About page at ${url.href} could not be fetched.`, error);
    return undefined;
  }
}

function read(): string | null {
  try {
    return window.localStorage.getItem(ABOUT_STORAGE_KEY);
  } catch {
    // Same reasoning as writing: an unreadable store means no remembered dismissal, which
    // is a state this already handles.
    return null;
  }
}
